import { createContext, useContext, useEffect } from 'react';

import { TreeData } from './types';
import { useDbtTree } from './useDbtTree';
import { useFavoriteTree } from './useFavoriteTree';
import { useWarehouseTree } from './useWarehouseTree';

type WarehouseTypes = 'snowflake';

type AccountNavigationContextType = {
  warehouses: Record<WarehouseTypes, TreeData>;
  dbtTree: TreeData;
  favoriteTree: TreeData;
};

const AccountNavigationContext = createContext<AccountNavigationContextType>({
  warehouses: {
    snowflake: {
      isLoading: false,
      initialized: false,
      generateChildrenForPath: () => {},
      fileTree: [],
      selectedFile: undefined,
      setSelectedFile: () => {},
      selectedFolder: undefined,
      setSelectedFolder: () => {},
      openDirectories: [],
      setOpenDirectories: () => {},
    },
  },
  dbtTree: {
    isLoading: false,
    initialized: false,
    generateChildrenForPath: () => {},
    fileTree: [],
    selectedFile: undefined,
    setSelectedFile: () => {},
    selectedFolder: undefined,
    setSelectedFolder: () => {},
    openDirectories: [],
    setOpenDirectories: () => {},
  },
  favoriteTree: {
    isLoading: false,
    initialized: false,
    generateChildrenForPath: () => {},
    fileTree: [],
    selectedFile: undefined,
    setSelectedFile: () => {},
    selectedFolder: undefined,
    setSelectedFolder: () => {},
    openDirectories: [],
    setOpenDirectories: () => {},
  },
});

export const AccountNavigationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dbtTree = useDbtTree();
  const snowflakeTree = useWarehouseTree();
  const favoriteTree = useFavoriteTree();

  useEffect(() => {
    if (snowflakeTree.selectedFile) {
      snowflakeTree.setSelectedFolder(undefined);
      dbtTree.setSelectedFile(undefined);
      dbtTree.setSelectedFolder(undefined);
    }
    // it's safe to disable since we only want to evaluate the above when the selectedFile/selectedFolder changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snowflakeTree.selectedFile]);

  useEffect(() => {
    if (snowflakeTree.selectedFolder) {
      snowflakeTree.setSelectedFile(undefined);
      dbtTree.setSelectedFile(undefined);
      dbtTree.setSelectedFolder(undefined);
    }
    // it's safe to disable since we only want to evaluate the above when the selectedFile/selectedFolder changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snowflakeTree.selectedFolder]);

  useEffect(() => {
    if (dbtTree.selectedFolder) {
      dbtTree.setSelectedFile(undefined);
      snowflakeTree.setSelectedFolder(undefined);
      snowflakeTree.setSelectedFile(undefined);
    }
    // it's safe to disable since we only want to evaluate the above when the selectedFile/selectedFolder changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbtTree.selectedFolder]);

  useEffect(() => {
    if (dbtTree.selectedFile) {
      dbtTree.setSelectedFolder(undefined);
      snowflakeTree.setSelectedFolder(undefined);
      snowflakeTree.setSelectedFile(undefined);
    }
    // it's safe to disable since we only want to evaluate the above when the selectedFile/selectedFolder changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbtTree.selectedFile]);

  return (
    <AccountNavigationContext.Provider
      value={{ dbtTree, warehouses: { snowflake: snowflakeTree }, favoriteTree }}
    >
      {children}
    </AccountNavigationContext.Provider>
  );
};

export const useAccountNavigationContext = () => {
  return useContext(AccountNavigationContext);
};
